import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { Constants } from '@praktio/common';
import { TextButton } from '@praktio/common-ui';
import { Id, ToastContainer, toast } from 'react-toastify';
import React, { ReactText, useEffect, useRef, useState } from 'react';

import Alert from '../../common/Alert/Alert';
import InteractiveDocument from '../../SlideAssets/InteractiveDocument';
import FeedbackBar from '../../InteractivePanel/FeedbackBar/FeedbackBar';

import { RootState } from '../../../ducks/configureStore';
import { showContactUs } from '../../../utils/learnerUtils';
import { SupplementalDocStates, WindowSizes } from '../../../utils/constants';

import 'react-toastify/dist/ReactToastify.css';
import './CBS.scss';

const { AnnotationTypes, ResourceItemTypes } = Constants;

const THRESHOLD = 40;

interface IProps {
  panelIds?: string[];
  hasLandscapeDoc: boolean;
  hasPortraitDoc: boolean;
  windowSize: WindowSizes;
  supplementalDoc?: SupplementalDoc;
  supplementalDocState?: SupplementalDocStates;
  setSupplementalDocState?: React.Dispatch<React.SetStateAction<SupplementalDocStates>>;
}

let inLMS = false;

try {
  inLMS = document?.referrer?.toLowerCase()?.startsWith('https://lms');
} catch (e) {
  console.error(e);
}

const CBS = ({
  panelIds,
  hasLandscapeDoc,
  hasPortraitDoc,
  windowSize,
  supplementalDoc,
  supplementalDocState,
  setSupplementalDocState,
}: IProps) => {
  // ===========================================================================
  // Redux
  // ===========================================================================

  const { activityComplete, feedbackText, explanationButtonsShouldPulse, correct, learnerTries } =
    useSelector((state: RootState) => {
      const { slidesLitmos, quizLitmos } = state;
      const { currentSlide } = slidesLitmos;
      const { explanationButtonsShouldPulse, completed } = quizLitmos;
      const { learnerData, timeline } = currentSlide || {};
      const { completed: activityComplete, correct, tries: learnerTries = 0 } = learnerData || {};
      const feedbackText = timeline?.find(
        (timelineElement) =>
          timelineElement.type === ResourceItemTypes.Annotation &&
          timelineElement.subType === AnnotationTypes.Correct,
      )?.text;

      return {
        activityComplete,
        feedbackText,
        explanationButtonsShouldPulse,
        completed,
        correct,
        learnerTries,
      };
    });

  // ===========================================================================
  // Refs
  // ===========================================================================

  const toastIdRef = useRef<ReactText | null>(null);

  // ===========================================================================
  // State
  // ===========================================================================

  const [showOverlay, setShowOverlay] = useState(false);
  const [startX, setStartX] = useState<number | null>(null);
  const [startY, setStartY] = useState<number | null>(null);

  // ===========================================================================
  // Functions
  // ===========================================================================

  const clearToastId = () => (toastIdRef.current = null);

  const resetStartPositions = () => {
    setStartX(null);
    setStartY(null);
  };

  const closeToast = () => {
    toast.dismiss(toastIdRef.current as string);
    clearToastId();
    setShowOverlay(false);
  };

  // ===========================================================================
  // Use Effects
  // ===========================================================================

  useEffect(() => {
    const doc = document.querySelector('.document') as HTMLElement;

    if (!doc) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }

      resetStartPositions();
      closeToast();
    };

    const handleMouseDown = (event: MouseEvent) => {
      setStartX(event.clientX);
      setStartY(event.clientY);
    };

    const handleMouseUp = () => {
      resetStartPositions();
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (e.buttons !== 1) {
        return;
      }

      // If no start position, don't do anything (this means the mouse was not clicked)
      if (startX === null || startY === null) {
        return;
      }

      const deltaX = Math.abs(e.clientX - startX);
      const deltaY = Math.abs(e.clientY - startY);

      // If the mouse has not moved far enough, don't do anything
      if (deltaX <= THRESHOLD && deltaY <= THRESHOLD) {
        return;
      }

      // If there is already a toast, don't show another one
      if (toastIdRef.current || toast.isActive(toastIdRef.current as Id)) {
        return;
      }

      const warningId = toast.info(
        <Alert
          variant="warning"
          heading="Highlighting Not Allowed"
          content={
            <>
              <p>
                It looks like you might be trying to highlight the text. Please note the
                instructions to <strong>click</strong> the correct location (<strong>not </strong>
                highlight).
              </p>
            </>
          }
          onDismiss={() => {
            toast.dismiss(warningId);
            clearToastId();
            setShowOverlay(false);
          }}
        />,
        {
          autoClose: false,
          className: 'no-default-styling',
          closeButton: false,
          closeOnClick: false,
          draggable: false,
          hideProgressBar: true,
          pauseOnHover: false,
          position: 'bottom-left',
          progress: undefined,
          onOpen: () => setShowOverlay(true),
        },
      );

      toastIdRef.current = warningId;
    };

    doc.addEventListener('mousedown', handleMouseDown);
    doc.addEventListener('mouseup', handleMouseUp);
    doc.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      doc.removeEventListener('mousedown', handleMouseDown);
      doc.removeEventListener('mouseup', handleMouseUp);
      doc.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('keydown', handleKeyDown);
    };

    // We don't need closeToast in the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startX, startY]);

  // Attach event listener to "Go to Selection" button to focus on active span character
  useEffect(() => {
    const btnGoToSelection = document.getElementById('CBS__GoToSelection');

    const handleClick = () => {
      const activeChar: HTMLSpanElement | null = document.querySelector('.active');

      if (!activeChar) {
        return;
      }

      activeChar.focus();
    };

    btnGoToSelection?.addEventListener('click', handleClick);

    return () => {
      btnGoToSelection?.removeEventListener('click', handleClick);
    };
  }, []);

  return (
    <div
      className={classNames('CBS', {
        'activity-complete': activityComplete,
        'has-supplemental-doc': supplementalDoc,
      })}
    >
      <header>
        <p id="CBS__Instructions" className="sr-only">
          Screen reader users: To make a selection, click or tap on characters within the document.
          Use the “Go to Selection” button to navigate directly to your last selected character. You
          can also navigate to the “Current Selection Announcement” region at any time to hear your
          current selection.
        </p>

        <button id="CBS__GoToSelection" className="sr-only" aria-describedby="CBS__Instructions">
          Go to Selection
        </button>
      </header>

      <div
        id="CBS__LiveRegionSelection"
        aria-atomic="true"
        className="sr-only"
        aria-live="assertive"
        aria-label="Current Selection"
        aria-describedby="CBS__Instructions"
      >
        No selection made yet
      </div>

      <div className="upper-content">
        <FeedbackBar windowSize={windowSize} />
        <div className="CBS-content">
          <InteractiveDocument
            panelIds={panelIds}
            layoutIsHorizontal={false}
            hasLandscapeDoc={hasLandscapeDoc}
            hasPortraitDoc={hasPortraitDoc}
            windowSize={windowSize}
            supplementalDoc={supplementalDoc}
            supplementalDocState={supplementalDocState}
            setSupplementalDocState={setSupplementalDocState}
          />
          <div className={classNames('CBS-feedback-panel')}>
            <div className="CBS-feedback-wrapper">
              <div
                className={classNames('CBS-feedback-panel-static', {
                  'd-none': !activityComplete,
                })}
              >
                <div
                  className={classNames('CBS-explanation', {
                    'should-pulse': explanationButtonsShouldPulse,
                  })}
                >
                  {feedbackText}
                </div>
              </div>
            </div>
            {inLMS && activityComplete && (!correct || learnerTries > 1) && (
              <div className="CBS-feedback-contact">
                <div className="CBS-feedback-contact-messaging">
                  Think you clicked a correct area
                  {correct && learnerTries > 1 ? ' on a prior try' : ''}? Submit a message with a
                  screenshot to tell us why.
                </div>
                <div className="CBS-feedback-send">
                  <TextButton
                    icon="chat_bubble_outline"
                    outlineIcon
                    onClick={(e) => {
                      showContactUs();
                    }}
                  >
                    Send a message
                  </TextButton>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        className={classNames('CBS--overlay', {
          visible: showOverlay,
        })}
      />

      <ToastContainer />
    </div>
  );
};

export default CBS;
